import { useState, useRef, useEffect } from 'react';
import { useSnackbar } from "notistack";
import { DOMAIN_API_HOST } from "../../../../../configs/main";
import { trackWizardEvent } from "../../../../../utils/productEvents";

const RETARGEO_DOMAIN = "lk.retargeo.ru";

const RETARGEO_VIDEO_MAX_BYTES = 20 * 1024 * 1024;

const RETARGEO_BANNER_SIZES = [
  { w: 240, h: 400, maxBytes: 120 * 1024 },
  { w: 300, h: 500, maxBytes: 120 * 1024 },
  { w: 300, h: 600, maxBytes: 120 * 1024 },
  { w: 300, h: 250, maxBytes: 120 * 1024 },
  { w: 728, h: 90, maxBytes: 120 * 1024 },
  { w: 1080, h: 607, maxBytes: 2 * 1024 * 1024 },
  { w: 600, h: 600, maxBytes: 2 * 1024 * 1024 },
  { w: 450, h: 450, maxBytes: 2 * 1024 * 1024 },
];

const getRetargeoBannerSpec = (width, height) =>
  RETARGEO_BANNER_SIZES.find((s) => s.w === width && s.h === height) ?? null;

const formatBytesLimit = (bytes) =>
  bytes >= 1024 * 1024 ? `${bytes / (1024 * 1024)} МБ` : `${Math.round(bytes / 1024)} КБ`;


export const BannerCarousel = ({ banners, onBannerClick, onDelete, allowDelete = true, isMobile = false }) => {
  const [dimensions, setDimensions] = useState({});

  useEffect(() => {
    const loadDimensions = async () => {
      const newDimensions = {};
      for (const banner of banners) {
        if (banner.extension && banner.extension.toLowerCase() !== "mp4") {
          const img = new Image();
          img.src = banner.url;
          await new Promise((resolve) => {
            img.onload = () => {
              newDimensions[banner.id] = `${img.width}×${img.height}`;
              resolve();
            };
            img.onerror = resolve;
          });
        } else {
          const video = document.createElement('video');
          video.src = banner.url;
          await new Promise((resolve) => {
            video.onloadedmetadata = () => {
              newDimensions[banner.id] = `${video.videoWidth}×${video.videoHeight}`;
              resolve();
            };
            video.onerror = resolve;
          });
        }
      }
      setDimensions(newDimensions);
    };
    loadDimensions();
  }, [banners]);

  return (
    <div>
      <div style={{
        display: banners.length > 0 ? "block" : "none",
        fontSize: isMobile ? 14 : 16,
        marginBottom: isMobile ? 10 : 0
      }}>
        Список добавленных ресайзов
      </div>
      <div
        style={{
          marginTop: 10,
          display: "flex",
          gap: 10,
          overflowX: "auto",
          width: isMobile ? "100%" : 750,
          paddingBottom: isMobile ? 10 : 0
        }}
      >
        {banners.map((banner) => (
          <div
            key={banner.id}
            style={{
              minWidth: isMobile ? 120 : 250,
              height: isMobile ? 120 : 250,
              borderRadius: 5,
              overflow: "hidden",
              cursor: "pointer",
              background: "#f0f1f3",
              position: "relative",
            }}
            onClick={() => onBannerClick(banner.id)}
          >
            {banner.extension && banner.extension.toLowerCase() !== "mp4" ? (
              <img
                alt="Banner"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
                src={banner.url}
              />
            ) : (
              <video
                src={banner.url}
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
                muted
                autoPlay
              />
            )}
            {allowDelete && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(banner.id);
                }}
                style={{
                  position: "absolute",
                  right: 5,
                  top: 5,
                  opacity: "0.7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  width: isMobile ? 25 : 30,
                  height: isMobile ? 25 : 30,
                  color: "rgba(255, 255, 255, .7)",
                  borderRadius: 5,
                  fontSize: 5,
                  cursor: "pointer",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={isMobile ? "20" : "24"}
                  height={isMobile ? "20" : "24"}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-x"
                >
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </div>
            )}
            <div
              style={{
                position: "absolute",
                right: 5,
                bottom: 5,
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                color: "white",
                padding: "2px 5px",
                borderRadius: 3,
                fontSize: isMobile ? 10 : 12,
                pointerEvents: "none",
              }}
            >
              {dimensions[banner.id] || "Загрузка..."}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


const BannerUpload = ({ onUpload, disabled = false, isMobile = false, signupDomain = null }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const isRetargeo = signupDomain === RETARGEO_DOMAIN;

  const handleFileChange = async (files) => {
    if (disabled) return;
    const newBanners = await Promise.all(
      Array.from(files).map(async (file) => {
        const url = URL.createObjectURL(file);
        let dimensions = null;

        if (file.type.startsWith('image/')) {
          dimensions = await new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
            img.onerror = () => resolve(null);
            img.src = url;
          });
        } else if (file.type.startsWith('video/')) {
          dimensions = await new Promise((resolve) => {
            const video = document.createElement('video');
            video.onloadedmetadata = () => {
              if (video.duration > 36) {
                enqueueSnackbar(`Длительность видео (${video.duration} сек) ${file.name} превышает лимит (35 сек)!`, { variant: 'error' });
                resolve(null);
                return;
              }
              resolve({ width: video.videoWidth, height: video.videoHeight });
            };
            video.onerror = () => resolve(null);
            video.src = url;
          });
        } else {
          enqueueSnackbar(`Недопустимый формат файла ${file.name}`, { variant: 'error' });
          URL.revokeObjectURL(url);
          return null;
        }

        if (!dimensions) {
          URL.revokeObjectURL(url);
          return null;
        }

        let maxSize = file.type.startsWith('image/') ? 20 * 1024 * 1024 : 50 * 1024 * 1024;

        if (isRetargeo) {
          const spec = getRetargeoBannerSpec(dimensions.width, dimensions.height);
          if (!spec) {
            const allowed = RETARGEO_BANNER_SIZES.map((s) => `${s.w}×${s.h}`).join(", ");
            const fileKind = file.type.startsWith('image/') ? 'Баннер' : 'Видео';
            enqueueSnackbar(
              `${fileKind} ${file.name} имеет недопустимый размер ${dimensions.width}×${dimensions.height}. Допускается: ${allowed}`,
              { variant: 'error' }
            );
            URL.revokeObjectURL(url);
            return null;
          }
          maxSize = file.type.startsWith('video/') ? RETARGEO_VIDEO_MAX_BYTES : spec.maxBytes;
        }

        if (file.size > maxSize) {
          enqueueSnackbar(
            `Размер файла ${file.name} превышает лимит ${formatBytesLimit(maxSize)}!`,
            { variant: 'error' }
          );
          URL.revokeObjectURL(url);
          return null;
        }

        return { url, type: file.type, file, dimensions };
      })
    );

    const validBanners = newBanners.filter(banner => banner !== null);

    onUpload(validBanners);

    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const handleUploadButtonClick = () => {
    if (disabled) return;
    fileInputRef.current.click();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileChange(e.dataTransfer.files);
  };

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      gap: 10,
      width: isMobile ? "100%" : "auto"
    }}>
      <div style={{ fontSize: isMobile ? 14 : 16 }}>Загрузить баннер</div>
      <label
        htmlFor="banner-upload"
        style={{
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? "none" : "auto",
          background: isDragging ? "#c8d4ff" : "#dee7ff",
          width: isMobile ? "100%" : "250px",
          height: isMobile ? "150px" : "250px",
          color: "#3f73f9",
          border: `1px dashed ${isDragging ? "#1a237e" : "#3f51b5"}`,
          borderRadius: 5,
          padding: isMobile ? 20 : 30,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 10,
          flexDirection: "column",
          transition: 'background 0.3s ease'
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: "none" }}
          id="banner-upload"
          multiple
          accept="image/*, video/*"
          onChange={(e) => handleFileChange(e.target.files)}
        />
        <p
          style={{
            textAlign: "center",
            fontSize: isMobile ? 11 : 12,
            margin: 0
          }}
        >
          Перетащите баннер из медиатеки или кликните для загрузки с вашего компьютера
        </p>
      </label>
      <button
        onClick={handleUploadButtonClick}
        disabled={disabled}
        style={{
          background: "#3f73f9",
          color: "white",
          padding: isMobile ? "8px 16px" : "10px 20px",
          borderRadius: 5,
          border: "none",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
          fontSize: isMobile ? 14 : 16,
          width: isMobile ? "100%" : "auto"
        }}
      >
        Загрузить
      </button>
    </div>
  );
};

export const Modal = ({ banner, onClose, isMobile = false }) => {
  if (!banner) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: isMobile ? 20 : 0
    }}
      onClick={onClose}
    >
      <div style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        padding: isMobile ? 10 : 20,
        borderRadius: 8,
        maxWidth: isMobile ? '100%' : '90%',
        maxHeight: isMobile ? '80%' : '90%',
        textAlign: 'center'
      }}
      >
        <div
          onMouseEnter={(e) => (e.currentTarget.style.color = "rgba(255, 255, 255, 1)")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255, 255, 255, .7)")}
          style={{
            position: 'absolute',
            right: isMobile ? -15 : -30,
            top: isMobile ? -15 : -20,
            opacity: '0.7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: isMobile ? 25 : 30,
            height: isMobile ? 25 : 30,
            color: 'rgba(255, 255, 255, .7)',
            borderRadius: 5,
            fontSize: 5,
            cursor: 'pointer'
          }}>
          <svg xmlns="http://www.w3.org/2000/svg" width={isMobile ? "24" : "32"} height={isMobile ? "24" : "32"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
        </div>
        {banner.extension && banner.extension.toLowerCase() !== "mp4" ? (
          <img
            src={banner.url}
            alt="Full banner"
            style={{
              maxWidth: isMobile ? '100%' : '800px',
              maxHeight: isMobile ? '400px' : '600px',
              objectFit: 'contain'
            }}
          />
        ) : (
          <video
            src={banner.url}
            controls
            autoPlay
            style={{
              maxWidth: isMobile ? '100%' : '800px',
              maxHeight: isMobile ? '400px' : '600px'
            }}
          >
          </video>
        )}
      </div>
    </div>
  );
};

export const BannerComponent = ({ campaign_id, upd, setUpdBanner, currentAdId, banners, setBanners, disabled, lockMessage, isMobile = false, signupDomain = null }) => {
  const [selectedBannerId, setSelectedBannerId] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const editLockMessage = lockMessage || "Можно изменять данные только у выключенного объявления";

  const handleUpload = async (newBanners) => {
    if (disabled) return enqueueSnackbar(editLockMessage, { variant: "info" });
    for (const fileObj of newBanners) {
      const data = new FormData();
      data.append("upload_file", fileObj.file);

      try {
        const response = await fetch(`${DOMAIN_API_HOST}/api/v1/advertisements/upload-banner?advert_id=${currentAdId}`, {
          method: "POST",
          body: data,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token")
          },
        });

        if (response.ok) {
          const result = await response.json();
          setBanners((prev) => [...prev, result]);
          setUpdBanner((value) => !value);
          trackWizardEvent("ads_upload", campaign_id);
          enqueueSnackbar("Баннер успешно загружен", { variant: "success" });
        } else {
          let errorMessage = "Ошибка при загрузке баннера";
          try {
            const errorData = await response.json();
            if (errorData?.detail) errorMessage = String(errorData.detail);
          } catch (_) { }
          enqueueSnackbar(errorMessage, { variant: "error" });
        }
      } catch (error) {
        console.log(error);
        enqueueSnackbar("Ошибка сети при загрузке баннера", { variant: "error" });
      }
    }
  };


  const handleDelete = async (bannerId) => {
    if (disabled) return enqueueSnackbar(editLockMessage, { variant: "info" });
    const result = window.confirm("Вы уверены, что хотите удалить баннер?");
    if (!result) return;
    try {
      const response = await fetch(`${DOMAIN_API_HOST}/api/v1/advertisements/delete-banner?_id=${bannerId}`, {
        method: "POST",
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token")
        },
      });

      if (response.ok) {
        setUpdBanner((value) => !value);
        setBanners((prev) => prev.filter((banner) => banner.id !== bannerId));
      } else {
        let errorMessage = response.status == "404" ? "Ошибка, баннер не найден" : "Ошибка при удалении баннера";
        try {
          const errorData = await response.json();
          if (errorData?.detail) errorMessage = String(errorData.detail);
        } catch (_) { }
        enqueueSnackbar(errorMessage, { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar("Ошибка сети при загрузке баннера", { variant: "error" });
    }
  };

  const handleClickCreative = async () => {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    myHeaders.append('Authorization', 'Bearer ' + localStorage.getItem("token"));
    const res = await fetch(`${DOMAIN_API_HOST}/api/v1/utils/help/banners/?campaign_id=${campaign_id}`, {
      method: 'POST',
      headers: myHeaders,
      body: JSON.stringify({})
    });
    if (res.ok) {
      enqueueSnackbar("Запрос принят! Скоро с вами свяжется наш менеджер", { variant: "success" })
    }
  }

  return (
    <div style={{
      display: "flex",
      gap: isMobile ? 20 : 30,
      position: "relative",
      flexDirection: isMobile ? "column" : "row",
      width: "100%"
    }}>
      <BannerUpload onUpload={handleUpload} disabled={disabled} isMobile={isMobile} signupDomain={signupDomain} />
      <BannerCarousel
        banners={banners}
        onBannerClick={setSelectedBannerId}
        onDelete={handleDelete}
        allowDelete={!disabled}
        isMobile={isMobile}
      />

      {signupDomain !== RETARGEO_DOMAIN && !isMobile && (
        <div style={{ position: "absolute", top: -110, right: -20 }}>
          <button style={{ padding: "5px 10px", fontSize: "16px", fontWeight: "500", borderRadius: 5, background: "#DFE8FF", color: "#3F73F9" }} onClick={() => { handleClickCreative() }}>Помочь с креативами</button>
        </div>
      )}

      {signupDomain !== RETARGEO_DOMAIN && isMobile && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: 10 }}>
          <button
            style={{
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "500",
              borderRadius: 5,
              background: "#DFE8FF",
              color: "#3F73F9",
              border: "none",
              cursor: "pointer",
            }}
            onClick={() => { handleClickCreative() }}
          >
            Помочь с креативами
          </button>
        </div>
      )}

      <Modal
        banner={selectedBannerId ? banners.find(banner => banner.id === selectedBannerId) : null}
        onClose={() => setSelectedBannerId(null)}
        isMobile={isMobile}
      />
    </div>
  );
};